import { useCallback, useEffect, useState } from "react";
import { Plus, Printer, Trash2, X, Settings, Loader2, Pencil, Download } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useReadOnly } from "../access/ReadOnly";
import {
    silentPrintBarcode,
    silentPrint,
    buildNamedBarcodeLabel,
    browserPrint,
    downloadLabelsPdf,
    getSavedLocalPrinter,
} from "./helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
} from "./helpers/networkPrint";
import PrinterSelectModal from "./PrinterSelectModal";
import { apiFetch } from "../api/apiFetch";

interface Desk {
    name: string;
    barcode: string;
    display_name_en?: string | null;
    display_name_pl?: string | null;
}

interface AllSettingsRow {
    warehouse: string;
    dispatch_desks?: Desk[];
}

// Print one named-barcode label, falling back through network → local Zebra
// (ZPL) → local HTML → browser dialog. Mirrors the pallet-receiving page's
// printOne helper so the UX is consistent across admin pages.
const printOneLabel = async (barcode: string, name: string): Promise<{ success: boolean; error?: string }> => {
    const html = buildNamedBarcodeLabel(barcode, name);
    const saved = getSavedNetworkPrinter();
    if (saved) {
        // Carry the desk name so the remote agent renders the named ZPL
        // layout matching the HTML label (agents ≥ 1.5.1 forward it).
        const result = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, barcode, false, { name });
        if (result.success) return { success: true };
    }
    const zebra = await silentPrintBarcode(barcode, 1, {
        name,
        fallbackHtml: html,
        printer: getSavedLocalPrinter() ?? undefined,
    });
    if (zebra.success) return { success: true };
    const local = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    if (local.success) return { success: true };
    return browserPrint(html);
};

const DesksAdmin = () => {
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [desksByWarehouse, setDesksByWarehouse] = useState<Record<string, Desk[]>>({});
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState<{ warehouse: string; name: string } | null>(null);
    const [editing, setEditing] = useState<{ warehouse: string; barcode: string; name: string; display_name_en: string; display_name_pl: string } | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    // In-app confirm modal for the destructive Remove action.
    const [removeConfirm, setRemoveConfirm] = useState<{ warehouse: string; desk: Desk } | null>(null);
    const [removing, setRemoving] = useState(false);
    // Print-job loading + failure state. See BoxesAdmin for the same pattern.
    const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
    const [printingAllWh, setPrintingAllWh] = useState<string | null>(null);
    const [downloadingWh, setDownloadingWh] = useState<string | null>(null);
    const [printerNeedsSetup, setPrinterNeedsSetup] = useState(false);

    // Load every warehouse's settings once so we know which warehouses exist
    // and can show a section per warehouse.
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/settings`, {});
            if (!res.ok) throw new Error("settings");
            const rows: AllSettingsRow[] = await res.json();
            const whList = rows.map(r => r.warehouse).sort();
            const map: Record<string, Desk[]> = {};
            for (const r of rows) map[r.warehouse] = r.dispatch_desks ?? [];
            setWarehouses(whList);
            setDesksByWarehouse(map);
        } catch {
            toast.error(t("server_error"), { toastId: "desks-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleAdd = async () => {
        if (!adding || !adding.name.trim() || readOnly) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/dispatch-admin/desks/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: adding.warehouse, name: adding.name.trim() }),
            });
            if (!res.ok) throw new Error("add");
            await refresh();
            setAdding(null);
        } catch {
            toast.error(t("server_error"), { toastId: "desk-add-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSave = async () => {
        if (!editing) return;
        if (!editing.name.trim() || readOnly) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/dispatch-admin/desks/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    warehouse: editing.warehouse,
                    barcode: editing.barcode,
                    name: editing.name.trim(),
                    display_name_en: editing.display_name_en.trim(),
                    display_name_pl: editing.display_name_pl.trim(),
                }),
            });
            if (!res.ok) throw new Error("update");
            await refresh();
            setEditing(null);
        } catch {
            toast.error(t("server_error"), { toastId: "desk-update-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const confirmRemove = async () => {
        if (!removeConfirm || readOnly) return;
        setRemoving(true);
        try {
            const res = await apiFetch(`/dispatch-admin/desks/remove`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    warehouse: removeConfirm.warehouse,
                    barcode: removeConfirm.desk.barcode,
                }),
            });
            if (!res.ok) throw new Error("remove");
            await refresh();
            setRemoveConfirm(null);
        } catch {
            toast.error(t("server_error"), { toastId: "desk-remove-err" });
        } finally {
            setRemoving(false);
        }
    };

    const handlePrint = async (desk: Desk) => {
        setPrintingBarcode(desk.barcode);
        try {
            const result = await printOneLabel(desk.barcode, desk.name);
            if (!result.success) {
                setPrinterNeedsSetup(true);
                toast.error(t("print_failed"), { toastId: "desk-print-err" });
            }
        } finally {
            setPrintingBarcode(null);
        }
    };

    const handlePrintAll = async (warehouse: string) => {
        const desks = desksByWarehouse[warehouse] ?? [];
        if (desks.length === 0) return;
        setPrintingAllWh(warehouse);
        try {
            let failures = 0;
            for (const d of desks) {
                const r = await printOneLabel(d.barcode, d.name);
                if (!r.success) failures += 1;
            }
            if (failures > 0) {
                setPrinterNeedsSetup(true);
                toast.warn(t("print_partial_failed", { failed: String(failures), total: String(desks.length) }));
            }
        } finally {
            setPrintingAllWh(null);
        }
    };

    const handleDownloadAll = async (warehouse: string) => {
        const desks = desksByWarehouse[warehouse] ?? [];
        if (desks.length === 0) return;
        setDownloadingWh(warehouse);
        try {
            const result = await downloadLabelsPdf(
                desks.map(d => ({ barcode: d.barcode, name: d.name })),
                `packing-desks-${warehouse}.pdf`,
            );
            if (!result.success) {
                toast.error(result.error || t("download_failed"), { toastId: "desk-pdf-err" });
            }
        } finally {
            setDownloadingWh(null);
        }
    };

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div>
            <div className="dispatch-admin-toolbar">
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm ${printerNeedsSetup ? "dispatch-admin-printer-needed" : ""}`}
                    onClick={() => { setShowPrinterSelect(true); setPrinterNeedsSetup(false); }}
                >
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
            </div>

            {warehouses.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}

            {warehouses.map(wh => {
                const desks = desksByWarehouse[wh] ?? [];
                return (
                    <details key={wh} className="admin-settings-po-warehouse">
                        <summary className="admin-settings-po-warehouse-summary">
                            <span className="admin-settings-po-warehouse-name">{wh}</span>
                            <span className="admin-settings-po-warehouse-count">
                                {desks.length}
                            </span>
                        </summary>
                        <div className="admin-settings-po-editor">
                            <div className="dispatch-admin-rows">
                                {desks.map(d => (
                                    <div key={d.barcode} className="dispatch-admin-row dispatch-admin-row--desk">
                                        <span className="dispatch-admin-row-name">{d.name}</span>
                                        <span className="dispatch-admin-row-barcode">{d.barcode}</span>
                                        <div className="dispatch-admin-row-actions-inline">
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => setEditing({
                                                    warehouse: wh,
                                                    barcode: d.barcode,
                                                    name: d.name,
                                                    display_name_en: d.display_name_en ?? "",
                                                    display_name_pl: d.display_name_pl ?? "",
                                                })}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("edit")}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => handlePrint(d)}
                                                title={t("print_label")}
                                                disabled={printingBarcode === d.barcode || printingAllWh === wh}
                                            >
                                                {printingBarcode === d.barcode || printingAllWh === wh
                                                    ? <Loader2 size={16} className="dispatch-admin-spin" />
                                                    : <Printer size={16} />}
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                onClick={() => setRemoveConfirm({ warehouse: wh, desk: d })}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("remove_desk")}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {desks.length === 0 && (
                                    <div className="admin-settings-empty">{t("no_desks_yet")}</div>
                                )}
                            </div>
                            <div className="dispatch-admin-row-actions">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setAdding({ warehouse: wh, name: "" })}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} />
                                    {t("add_desk")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handlePrintAll(wh)}
                                    disabled={desks.length === 0 || printingAllWh === wh}
                                >
                                    {printingAllWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Printer size={14} />}
                                    {t("print_all")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handleDownloadAll(wh)}
                                    disabled={desks.length === 0 || downloadingWh === wh}
                                >
                                    {downloadingWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Download size={14} />}
                                    {t("download_all")}
                                </button>
                            </div>
                        </div>
                    </details>
                );
            })}

            {adding && (
                <div className="modal-overlay" onClick={() => !submitting && setAdding(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("add_desk")} — {adding.warehouse}</h2>
                            <button className="modal-close" onClick={() => setAdding(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("desk_name")}
                                <input
                                    type="text"
                                    autoFocus
                                    className="dispatch-admin-form-input"
                                    value={adding.name}
                                    onChange={e => setAdding({ ...adding, name: e.target.value })}
                                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                                    placeholder="DESK 1"
                                />
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setAdding(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleAdd}
                                    disabled={submitting || !adding.name.trim() || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("add_desk")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="modal-overlay" onClick={() => !submitting && setEditing(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("edit_desk")} — {editing.barcode}</h2>
                            <button className="modal-close" onClick={() => setEditing(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("desk_name")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.name}
                                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("desk_display_name_en")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.display_name_en}
                                    onChange={e => setEditing({ ...editing, display_name_en: e.target.value })}
                                    placeholder={editing.name}
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("desk_display_name_pl")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.display_name_pl}
                                    onChange={e => setEditing({ ...editing, display_name_pl: e.target.value })}
                                    placeholder={editing.name}
                                />
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setEditing(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleEditSave}
                                    disabled={submitting || !editing.name.trim() || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {removeConfirm && (
                <div className="modal-overlay" onClick={() => !removing && setRemoveConfirm(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("remove_desk")}</h2>
                            <button className="modal-close" onClick={() => setRemoveConfirm(null)} disabled={removing}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                                {t("confirm_remove_desk", { name: removeConfirm.desk.name })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => setRemoveConfirm(null)}
                                    disabled={removing}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={confirmRemove}
                                    disabled={removing || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {removing ? t("submitting") : t("remove_desk")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default DesksAdmin;
